import { useEffect } from 'react'
import { Link, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Lock } from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useCurrentTeam } from '@/features/team/current-team'
import { navigation, type NavEntry } from './navigation'
import { TeamSwitcher } from './team-switcher'
import { PlanUsage } from './plan-usage'
import { UserMenu } from './user-menu'

export function AppSidebar() {
  const { t } = useTranslation()
  const { isAdmin } = useCurrentTeam()
  const { pathname } = useLocation()
  const { setOpenMobile } = useSidebar()

  // on a phone the menu is a sheet over the page; going somewhere closes it
  useEffect(() => setOpenMobile(false), [pathname, setOpenMobile])

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>

      <SidebarContent>
        {navigation
          .filter((group) => !group.adminOnly || isAdmin)
          .map((group) => (
            <SidebarGroup key={group.id}>
              <SidebarGroupLabel>{t(group.title)}</SidebarGroupLabel>
              <SidebarMenu>
                {group.items.map((item) => (item.children ? <NavCollapsible key={item.id} item={item} /> : <NavItem key={item.id} item={item} />))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
      </SidebarContent>

      <SidebarFooter>
        {isAdmin && <PlanUsage />}
        <UserMenu />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

function NavItem({ item }: { item: NavEntry }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const url = item.url!
  const active = url === '/' ? pathname === '/' : pathname === url || pathname.startsWith(url + '/')
  const Icon = item.icon

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={t(item.title)}>
        <Link to={url}>
          <Icon />
          <span>{t(item.title)}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function NavCollapsible({ item }: { item: NavEntry }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { hasFeature } = useCurrentTeam()
  const children = item.children ?? []
  const containsActive = children.some((c) => pathname === c.url || pathname.startsWith(c.url + '/'))
  const Icon = item.icon
  const { state, isMobile } = useSidebar()

  // Collapsed to icons, the sidebar hides sub-menus, so the children open as a flyout instead
  if (state === 'collapsed' && !isMobile) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton tooltip={t(item.title)} isActive={containsActive}>
              <Icon />
              <span>{t(item.title)}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" sideOffset={8} className="min-w-52">
            <DropdownMenuLabel>{t(item.title)}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {children.map((child) => (
              <DropdownMenuItem key={child.id} asChild className={pathname === child.url ? 'bg-accent font-medium' : undefined}>
                <Link to={child.url}>
                  <span>{t(child.title)}</span>
                  {!hasFeature(child.requiredFeature) && <Lock aria-label={t('v2.nav.premium')} className="ml-auto size-3.5 text-muted-foreground" />}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    )
  }

  return (
    <Collapsible asChild defaultOpen={containsActive} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={t(item.title)} isActive={containsActive}>
            <Icon />
            <span>{t(item.title)}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {children.map((child) => {
              const locked = !hasFeature(child.requiredFeature)
              return (
                <SidebarMenuSubItem key={child.id}>
                  <SidebarMenuSubButton asChild isActive={pathname === child.url}>
                    <Link to={child.url}>
                      <span>{t(child.title)}</span>
                      {locked && <Lock aria-label={t('v2.nav.premium')} className="ml-auto size-3.5! text-muted-foreground!" />}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
